import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../database/useAuth";
import StandardLayout from "../../components/StandardLayout";
import Loading from "../../components/Loading";
import AdminControls from "./components/AdminControls";
import ExecutionHistoryTable from "./components/ExecutionHistoryTable";
import StatsCards from "./components/StatsCards";
import ParserStatusIndicator from "./components/ParserStatusIndicator";
import UserManagement from "./components/UserManagement";
import DuplicateManagement from "./components/DuplicateManagement";
import AdminLeftMenu from "./leftMenu/AdminLeftMenu";
import { useAdminData } from "./hooks/useAdminData";
import styled from "styled-components";
import { COLORS } from "../../helpers/designConstants";
import { getFunctions, httpsCallable } from "firebase/functions";

const AdminContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    padding: 16px;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    padding: 8px;
    max-width: 100%;
  }
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 600;
  color: ${COLORS.textPrimary};
  margin: 0 0 8px 0;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
  
  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: ${COLORS.textSecondary};
  margin: 0;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 24px;
  margin-bottom: 32px;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  @media (max-width: 768px) {
    gap: 16px;
    margin-bottom: 24px;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Section = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 24px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 8px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${COLORS.textPrimary};
  margin: 0 0 16px 0;
  
  @media (max-width: 768px) {
    font-size: 18px;
    margin: 0 0 12px 0;
  }
`;

// Supabase Migration Sync - syncs Firebase data to Supabase
const MigrationTestButtons = () => {
  const [loading, setLoading] = useState(null);
  const [results, setResults] = useState({});
  const functions = getFunctions();

  const runBackfill = async (functionName) => {
    // Confirmation dialog
    const isUsers = functionName.includes("backfillUsers");
    const isAuthMigration = functionName.includes("backfillSupabaseAuth");

    let confirmMessage;
    if (isAuthMigration) {
      confirmMessage = "This will migrate Firebase Auth users to Supabase Auth.\n\nThis operation:\n• Creates Supabase Auth accounts for all Firebase users\n• Users will need to use Email OTP or set a password to log in\n• Links user profiles to new Supabase auth user IDs\n• Requires user profiles to be synced first\n• Is safe to run multiple times (idempotent)\n\nProceed?";
    } else if (isUsers) {
      confirmMessage = "This will sync ALL Firebase users to Supabase.\n\nThis operation:\n• Creates/updates profiles in Supabase\n• Maps Firebase UIDs to Supabase UUIDs\n• Is safe to run multiple times (idempotent)\n\nProceed?";
    } else {
      confirmMessage = "This will sync ALL Firebase projects to Supabase.\n\nThis operation:\n• Creates/updates projects in Supabase\n• Syncs project members, items, and warehouses\n• Requires users to be synced first\n• Is safe to run multiple times (idempotent)\n\nProceed?";
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(functionName);
    try {
      const fn = httpsCallable(functions, functionName);
      const result = await fn();
      setResults(prev => ({ ...prev, [functionName]: result.data }));
      console.log(`✅ ${functionName}:`, result.data);

      let successMessage;
      if (isAuthMigration) {
        successMessage = `Auth migration completed!\n\nTotal: ${result.data.totalUsers}\nSucceeded: ${result.data.totalSucceeded}\nFailed: ${result.data.totalFailed}\nAuth users created: ${result.data.authUsersCreated || 0}`;
      } else if (isUsers) {
        successMessage = `Users synced successfully!\n\nTotal: ${result.data.totalUsers}\nSucceeded: ${result.data.totalSucceeded}\nFailed: ${result.data.totalFailed}`;
      } else {
        successMessage = `Projects synced successfully!\n\nTotal: ${result.data.totalProjects}\nSucceeded: ${result.data.totalSucceeded}\nFailed: ${result.data.totalFailed}\nUser mappings: ${result.data.userMappingsFound}/${result.data.userMappingsTotal}`;
      }

      alert(successMessage);
    } catch (error) {
      console.error(`❌ ${functionName}:`, error);
      alert(`Sync failed:\n\n${error.message}`);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <p style={{ fontSize: "12px", color: "#92400e", margin: "0 0 4px 0" }}>
        Migrate Firebase data to Supabase. Run in order.
      </p>
      <button
        onClick={() => runBackfill("migration-backfillUsers")}
        disabled={loading}
        style={{
          padding: "8px 16px",
          backgroundColor: loading === "migration-backfillUsers" ? "#9ca3af" : "#3b82f6",
          color: "white",
          border: "none",
          borderRadius: "4px",
          cursor: loading ? "not-allowed" : "pointer",
          fontWeight: "500",
        }}
      >
        {loading === "migration-backfillUsers" ? "Syncing Users..." : "1. Sync Users"}
      </button>
      <button
        onClick={() => runBackfill("migration-backfillProjects")}
        disabled={loading}
        style={{
          padding: "8px 16px",
          backgroundColor: loading === "migration-backfillProjects" ? "#9ca3af" : "#10b981",
          color: "white",
          border: "none",
          borderRadius: "4px",
          cursor: loading ? "not-allowed" : "pointer",
          fontWeight: "500",
        }}
      >
        {loading === "migration-backfillProjects" ? "Syncing Projects..." : "2. Sync Projects"}
      </button>
      <button
        onClick={() => runBackfill("migration-backfillSupabaseAuth")}
        disabled={loading}
        style={{
          padding: "8px 16px",
          backgroundColor: loading === "migration-backfillSupabaseAuth" ? "#9ca3af" : "#8b5cf6",
          color: "white",
          border: "none",
          borderRadius: "4px",
          cursor: loading ? "not-allowed" : "pointer",
          fontWeight: "500",
        }}
      >
        {loading === "migration-backfillSupabaseAuth" ? "Migrating Auth..." : "3. Migrate Firebase Auth → Supabase"}
      </button>
      {(results["migration-backfillUsers"] || results["migration-backfillProjects"] || results["migration-backfillSupabaseAuth"]) && (
        <div style={{ fontSize: "11px", color: "#065f46", marginTop: "4px", padding: "8px", backgroundColor: "#d1fae5", borderRadius: "4px" }}>
          {results["migration-backfillUsers"] && (
            <div>✓ Users: {results["migration-backfillUsers"].totalSucceeded}/{results["migration-backfillUsers"].totalUsers}</div>
          )}
          {results["migration-backfillProjects"] && (
            <div>✓ Projects: {results["migration-backfillProjects"].totalSucceeded}/{results["migration-backfillProjects"].totalProjects}</div>
          )}
          {results["migration-backfillSupabaseAuth"] && (
            <div>✓ Auth: {results["migration-backfillSupabaseAuth"].authUsersCreated || results["migration-backfillSupabaseAuth"].totalSucceeded}/{results["migration-backfillSupabaseAuth"].totalUsers}</div>
          )}
        </div>
      )}
    </div>
  );
};

const AdminPage = () => {
  const { user, authContextLoaded } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // Always call useAdminData hook, but conditionally enable data fetching
  const {
    status,
    recentExecutions,
    stats,
    users,
    isSystemInitialized,
    isLoading,
    error,
    triggerManualRun
  } = useAdminData(user?.isSuperAdmin, authContextLoaded);
  
  // Determine if this is initial loading (no data yet) vs action loading (have data, performing action)
  const isInitialLoading = isLoading && (!stats && !users.length && !recentExecutions.length);

  // Show loading while auth context loads
  if (!authContextLoaded) {
    return <Loading />;
  }

  // Redirect non-authenticated users
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Show loading while checking initialization status (but only if no error)
  if (isSystemInitialized === null && !error) {
    return <Loading />;
  }

  // Redirect non-superAdmin users to 404 ONLY if system is already initialized
  if (!user.isSuperAdmin && isSystemInitialized) {
    return <Navigate to="/not_found" replace />;
  }
  
  // If we have an error but no system initialization info yet, assume super admin should see dashboard
  if (isSystemInitialized === null && error && user.isSuperAdmin) {
    // Allow super admin to see dashboard even with errors
    console.log('[AdminPage] Showing dashboard despite initialization error for super admin');
  }

  if (isInitialLoading) {
    return (
      <StandardLayout
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        leftMenuContent={<AdminLeftMenu onMenuClose={() => setIsMenuOpen(false)} />}
      >
        <Loading />
      </StandardLayout>
    );
  }

  return (
    <StandardLayout
      isMenuOpen={isMenuOpen}
      setIsMenuOpen={setIsMenuOpen}
      leftMenuContent={<AdminLeftMenu onMenuClose={() => setIsMenuOpen(false)} />}
    >
      <AdminContainer>
        <Header>
          <Title>System Administration</Title>
          <Subtitle>
            Data parser management and system monitoring
          </Subtitle>
        </Header>

        {error && (
          <Section style={{ backgroundColor: "#fef2f2", borderColor: "#fecaca" }}>
            <p style={{ color: "#dc2626", margin: 0 }}>
              Error loading admin data: {error}
            </p>
          </Section>
        )}

        {/* Show full admin interface for superAdmin users, or just user management for initialization */}
        {user.isSuperAdmin ? (
          <GridContainer>
            <MainContent>
              <Section>
                <SectionTitle>System Statistics</SectionTitle>
                <StatsCards stats={stats} isLoading={isLoading} />
              </Section>

              <Section>
                <SectionTitle>Execution History</SectionTitle>
                <ExecutionHistoryTable 
                  executions={recentExecutions} 
                  isLoading={isLoading}
                />
              </Section>

              <Section>
                <SectionTitle>User Management</SectionTitle>
                <UserManagement users={users} />
              </Section>

              <Section>
                <SectionTitle>Duplicate Management</SectionTitle>
                <DuplicateManagement />
              </Section>
            </MainContent>

            <Sidebar>
              <Section>
                <SectionTitle>Parser Status</SectionTitle>
                <ParserStatusIndicator 
                  status={status}
                  isLoading={isLoading}
                />
              </Section>

              <Section>
                <SectionTitle>Manual Controls</SectionTitle>
                <AdminControls
                  onTriggerRun={triggerManualRun}
                  isRunning={status?.isRunning}
                  isLoading={isLoading}
                />
              </Section>

              {/* Supabase Migration Sync */}
              <Section style={{ backgroundColor: "#fef3c7", border: "1px solid #f59e0b" }}>
                <SectionTitle style={{ color: "#92400e" }}>Supabase Sync</SectionTitle>
                <MigrationTestButtons />
              </Section>
            </Sidebar>
          </GridContainer>
        ) : (
          // Show only user management for first-time initialization
          <Section>
            <SectionTitle>User Management</SectionTitle>
            <UserManagement users={users} />
          </Section>
        )}
      </AdminContainer>
    </StandardLayout>
  );
};

export default AdminPage;